import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../core/api/api.service';
import { ResourceUpload } from '../../core/firebase/resource-upload';
import { ModuleStateService } from '../../module-state.service';
import { SmService } from 'supermappe-core';
import { InputDialogService } from '../../shared/dialog/input-dialog.service';
import { Observable, Observer, firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { extract } from '../../core/i18n.service';
import { Logger } from '../../core/logger.service';
import { ConfirmationService } from '../../shared/dialog/confirmation.service';
import { ImageUtilsService } from '../../core/image-utils-service.service';
import { STATS } from '../../../../../commondata/stats/stats-constants';
import { StatDto } from '../../core/stats/stat-dto';
import { MatomoTracker } from 'ngx-matomo-client';

const logger = new Logger('ImageMapsService');

@Injectable({
  providedIn: 'root',
})
export class ImageMapsService {
  private smService = inject(SmService);
  private moduleStateService = inject(ModuleStateService);
  private translateService = inject(TranslateService);
  private apiService = inject(ApiService);
  private inputDialogService = inject(InputDialogService);
  private confirmationService = inject(ConfirmationService);
  private imageUtils = inject(ImageUtilsService);
  private tracker = inject(MatomoTracker);

  private resizeImageLimit: any = 1500;

  private resolveTargetNode(nodeOutside?: any, targetNodeIdOutside?: any): any {
    let targetNodeId: any = '';
    let data: any = null;
    let nodeCreatedOutside: any = false;

    if (targetNodeIdOutside) {
      nodeCreatedOutside = true;
      targetNodeId = targetNodeIdOutside;
    } else if (nodeOutside) {
      data = nodeOutside;
      targetNodeId = data.node ? data.node.id : data.id;
    } else {
      data = this.getNode();
      targetNodeId = data.node ? data.node.id : data.id;
    }

    return { targetNodeId, data, nodeCreatedOutside };
  }

  private showOfflineConfirmation(): any {
    const errorMessage = this.translateService.instant(extract('OFFLINE_GENERIC'));
    this.moduleStateService.dropping = false;
    this.moduleStateService.lockUI(false, false);

    this.confirmationService.confirm(
      this.translateService.instant(extract('WARNING')),
      this.translateService.instant(extract(errorMessage)),
      this.translateService.instant(extract('BUTTON_OK')),
      '',
    );
    return errorMessage;
  }

  private handleErrorAndRevert(
    error: any,
    targetNodeId: any,
    data: any,
    nodeCreatedOutside?: any,
    showConfirmation: any = false,
    skipConfirmForUri?: any,
  ): any {
    let errorMessage = '';

    if (error?.status === 504) {
      errorMessage = this.translateService.instant(extract('OFFLINE_GENERIC'));
    } else {
      errorMessage = error?.code || 'ERROR_INSERT_IMAGE';
    }

    this.smService.setImagePlaceholderToNodeId(targetNodeId, false);
    this.moduleStateService.dropping = false;
    this.moduleStateService.lockUI(false, false);

    const shouldRevertSelection = data?.created || nodeCreatedOutside;

    if (showConfirmation) {
      if (
        !skipConfirmForUri ||
        !skipConfirmForUri.startsWith('https://lh3.googleusercontent.com/d/')
      ) {
        this.confirmationService
          .confirm(
            this.translateService.instant(extract('WARNING')),
            this.translateService.instant(extract(errorMessage)),
            this.translateService.instant(extract('BUTTON_OK')),
            '',
          )
          .subscribe(() => {
            if (shouldRevertSelection) {
              this.smService.deleteSelection();
            }
          });
      }
    } else {
      this.moduleStateService.setStateError(errorMessage);
      if (shouldRevertSelection) {
        this.smService.deleteSelection();
      }
    }

    return errorMessage;
  }

  private getNode(): any {
    const result: any = { node: null, created: false };
    result.node = this.smService.getSelectedNode();
    if (result.node === null) {
      result.node = this.smService.addNodeAndSelect(
        this.moduleStateService.selectionData.scrCenterX,
        this.moduleStateService.selectionData.scrCenterY,
      );
      this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.NODE_CREATE));
      result.created = true;
    }
    return result;
  }

  /*resizeImage(file: any): Observable<any> {
    return this.ng2ImgMax.resizeImage(file, this.resizeImageLimit, this.resizeImageLimit);
  }*/

  async insertImageFromFile(currentImageFile: any, node: any) {
    if (!currentImageFile) return;

    try {
      // Usa resizeImage dal TUO imageUtils (di default a WebP per prestazioni ottimali)
      const resizedImage = await this.imageUtils.resizeImage(
        currentImageFile,
        this.resizeImageLimit,
      );

      await this.uploadImageFromFile(resizedImage, node);
    } catch (error) {
      console.log('Errore durante il resize:', error);
      // Fallback: se il resize fallisce, prova a caricare l'immagine originale
      await this.uploadImageFromFile(currentImageFile, node);
    }
  }

  async uploadImageFromFile(compressedImage: any, node: any) {
    const mapId = this.moduleStateService.docId;
    const { targetNodeId, data } = this.resolveTargetNode(node);

    if (!targetNodeId) {
      this.moduleStateService.setStateError('ERROR_INSERT_IMAGE');
      if (data?.created) this.smService.deleteSelection();
      return;
    }

    this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
    const currentImageUpload = new ResourceUpload(compressedImage);

    try {
      const image: any = await firstValueFrom(
        this.apiService.insertImage(currentImageUpload, mapId, targetNodeId),
      );
      this.insertImageFromUri(image.url, image.destPath, targetNodeId, data?.created);
    } catch (error: any) {
      console.error(error);
      this.handleErrorAndRevert(error, targetNodeId, data, false, false);
    }
  }

  async insertImageFromSvg(svg: any, targetNodeIdOutside: any) {
    if (!svg) return;

    const mapId = this.moduleStateService.docId;
    this.smService.setImagePlaceholderToNodeId(targetNodeIdOutside, true);

    try {
      const image: any = await firstValueFrom(
        this.apiService.insertImageFromSvg(svg, mapId, targetNodeIdOutside),
      );
      this.insertImageFromUri(image.url, image.destPath, targetNodeIdOutside, true);
    } catch (error: any) {
      this.handleErrorAndRevert(error, targetNodeIdOutside, null, false, false);
    }
  }

  openDialog(urlToOpen: any): Observable<any> {
    return new Observable((observer: Observer<any>) => {
      this.moduleStateService.setFocusOnMap(false);
      this.moduleStateService.lockUI(false, false);

      this.inputDialogService
        .askUrl(
          this.translateService.instant(extract('INSERT-IMAGE')),
          this.translateService.instant(extract('INPUT-IMAGE-URL')),
          '',
          this.moduleStateService.docId,
          urlToOpen,
        )
        .subscribe({
          next: (result: any) => {
            this.moduleStateService.setFocusOnMap(true);
            observer.next(result === '' ? false : result);
            observer.complete();
          },
          error: () => {
            this.moduleStateService.setFocusOnMap(true);
            observer.next(false);
            observer.complete();
          },
        });
    });
  }

  async insertImageFromDrive(
    googleId: any,
    storagePath?: any,
    targetNodeIdOutside?: any,
    nodeCreatedOutside?: any,
  ) {
    if (!navigator.onLine) {
      this.showOfflineConfirmation();
      return;
    }

    const {
      targetNodeId,
      data,
      nodeCreatedOutside: createdOutside,
    } = this.resolveTargetNode(null, targetNodeIdOutside);
    const isNodeNew = nodeCreatedOutside || createdOutside;

    if (!targetNodeId) {
      this.moduleStateService.dropping = false;
      return;
    }

    this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
    const mapId = this.moduleStateService.docId;
    this.moduleStateService.lockUI(true, true);

    try {
      const imageData: any = await firstValueFrom(
        this.apiService.insertImageFromDrive(googleId, mapId, targetNodeId, storagePath),
      );
      const imageInfo = { name: imageData.res, transparent: false, custom: false };

      this.smService.setImageToNodeId(targetNodeId, imageInfo, false);
      this.moduleStateService.lockUI(false, false);
      this.moduleStateService.dropping = false;
      this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.IMAGE_INSERT));
    } catch (error: any) {
      this.handleErrorAndRevert(error, targetNodeId, data, isNodeNew, true);
    }
  }

  async insertImageFromUriWithDialog() {
    this.moduleStateService.lockUI(true, true);

    const result: any = await firstValueFrom(this.openDialog(''));
    if (result) {
      this.insertImageFromUri(result.name);
    } else {
      this.moduleStateService.lockUI(false, false);
    }
  }

  async insertImageFromUri(
    uri: any,
    storagePath?: any,
    targetNodeIdOutside?: any,
    nodeCreatedOutside?: any,
    callback?: any,
  ): Promise<any> {
    if (!navigator.onLine) {
      const errorMessage = this.showOfflineConfirmation();
      if (callback) callback(false, errorMessage);
      return null;
    }

    const {
      targetNodeId,
      data,
      nodeCreatedOutside: createdOutside,
    } = this.resolveTargetNode(null, targetNodeIdOutside);
    const isNodeNew = nodeCreatedOutside || createdOutside;

    if (!targetNodeId) {
      this.moduleStateService.dropping = false;
      if (callback) callback(false, 'No target node ID found.');
      return null;
    }

    this.smService.setImagePlaceholderToNodeId(targetNodeId, true);
    const mapId = this.moduleStateService.docId;

    try {
      const imageData: any = await firstValueFrom(
        this.apiService.uploadImageFromUri(uri, mapId, targetNodeId, storagePath),
      );
      const imageInfo = { name: imageData.res, transparent: false, custom: false };

      this.smService.setImageToNodeId(targetNodeId, imageInfo, false);
      this.moduleStateService.lockUI(false, false);
      this.moduleStateService.dropping = false;

      if (this.moduleStateService.mapOperations) {
        this.moduleStateService.mapOperations.push(new StatDto(STATS.MAP.IMAGE_INSERT));
      }

      try {
        console.log('MATOMO event Map insert image');
        this.tracker.trackEvent('Map', 'Insert image', 'InsertImage');
      } catch (e: any) {
        logger.error('matomo error ' + e.message);
      }

      if (callback) callback(true, imageInfo);
      return imageInfo;
    } catch (error: any) {
      const errorMessage = this.handleErrorAndRevert(
        error,
        targetNodeId,
        data,
        isNodeNew,
        true,
        uri,
      );
      if (callback) callback(false, errorMessage);
      return null;
    }
  }

  insertImageFromUriPromise(
    uri: any,
    storagePath?: any,
    targetNodeIdOutside?: any,
    nodeCreatedOutside?: any,
  ): Promise<any> {
    return this.insertImageFromUri(uri, storagePath, targetNodeIdOutside, nodeCreatedOutside);
  }
}
